import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Notification} from '@Components/notification';
import {openEditAudioItemModal} from '@Modals/edit-audio-item-modal';
import {getNumberOfAudioItemsInPoster} from '@Components/poster-editor/library/poster-editor-library';
import {updateBottomWebSeekbarState} from '@Components/poster-editor/poster-editor-reducer';
import {fireGaEventForTimelineOpenFromAudioIcon} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import styles from './audio-playlist-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

interface AudioPlaylistItemProps {
  isSmallScreen?: boolean;
}

export function AudioPlaylistItem({isSmallScreen = false}: AudioPlaylistItemProps): ReactElement | null {
  const isAudioPlaylistSelected = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID !== '';
  });
  const isSidebarSmall = useAppSelector((state) => {
    return state.posterEditor.isSidebarSmall;
  });
  const isSidebarOpen = useAppSelector((state) => {
    return state.posterEditor.isSidebarOpen;
  });
  const isWebBottomBarExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const dispatch = useAppDispatch();

  const selectAudioPlaylist = (): void => {
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      return;
    }
    poster.audioClips.selectFirstAudioItem();
  };

  const unSelectAudioPlaylist = (): void => {
    window.posterEditor?.whiteboard?.audioClips.unselectAudioPlaylist();
    dispatch(updateBottomWebSeekbarState(false));
  };

  const openTimelineIfNotOpenedAlready = (): void => {
    if (!isWebBottomBarExpanded) {
      dispatch(updateBottomWebSeekbarState(true));
      fireGaEventForTimelineOpenFromAudioIcon();
    }
  };

  const onClickHandler = (): void => {
    if (isAudioPlaylistSelected) {
      unSelectAudioPlaylist();
    } else {
      openTimelineIfNotOpenedAlready();
      selectAudioPlaylist();
    }
  };

  const onClickHandlerMobile = (): void => {
    if (isAudioPlaylistSelected) {
      unSelectAudioPlaylist();
    } else {
      selectAudioPlaylist();

      openEditAudioItemModal({
        panelId: 'edit-audio-item-legacy-panel',
        onClose: unSelectAudioPlaylist,
      });
    }
  };

  const getMobileAudioPlaylistItem = (): ReactElement => {
    return (
      <div className={styles.audioIconContainer}>
        <Notification className={styles.notification} text={getNumberOfAudioItemsInPoster().toString()} />
        <Icon onClick={onClickHandlerMobile} shape={IconShape.CIRCLE} type={IconType.SHADOW} icon="icon-audio" size={IconSize.SIZE_ICON_20} />
      </div>
    );
  };

  const getGeneralAudioPlaylistItem = (): ReactElement => {
    return (
      <div className={`js-hide ${styles.container} ${!isSidebarOpen && isSidebarSmall ? styles.collapsedSidebar : ''}`}>
        <Icon
          onClick={onClickHandler}
          shape={IconShape.CIRCLE}
          type={isAudioPlaylistSelected ? IconType.PRIMARY : IconType.SHADOW}
          icon="icon-audio"
          size={IconSize.SIZE_ICON_24}
        />
      </div>
    );
  };

  const getAudioPlaylistItem = (): ReactElement | null => {
    if (getNumberOfAudioItemsInPoster() === 0) {
      return null;
    }
    if (isSmallScreen) {
      return getMobileAudioPlaylistItem();
    }
    return getGeneralAudioPlaylistItem();
  };

  return getAudioPlaylistItem();
}
